import React, { useState, useEffect } from 'react';
import axios from 'axios';

function AdminDashboard({ onBackToDashboard, onShowEditDashboard }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]); // Store all users for stats

  useEffect(() => {
    const role = localStorage.getItem('role');
    setCurrentUserRole(role);
    fetchUsers();
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        process.env.REACT_APP_API_URL + '/admin/users',
        // 'http://localhost:4001/admin/users',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            email: searchTerm || undefined,
          },
        }
      );
      setUsers(response.data);
      
      // Always fetch all users for accurate stats when there's no search term
      if (!searchTerm) {
        setAllUsers(response.data);
      } else {
        // When searching, we need to fetch all users separately for stats
        const allUsersResponse = await axios.get(
          process.env.REACT_APP_API_URL + '/admin/users',
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            // No search params to get all users
          }
        );
        setAllUsers(allUsersResponse.data);
      }
      setError('');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleRoleChange = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowRoleModal(true);
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`Are you sure you want to delete user: ${userEmail}?`)) {
      setActionLoading(true);
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/admin/users/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        fetchUsers();
        alert('User deleted successfully!');
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete user');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const submitPasswordChange = async () => {
    if (!newPassword.trim()) {
      alert('Please enter a new password');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/users/${selectedUser.id}/password`,
        { newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setShowPasswordModal(false);
      alert('Password updated successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update password');
    } finally {
      setActionLoading(false);
    }
  };

  const submitRoleChange = async () => {
    if (newRole === selectedUser.role) {
      setShowRoleModal(false);
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/users/${selectedUser.id}/role`,
        { newRole },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setShowRoleModal(false);
      fetchUsers();
      alert('Role updated successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update role');
    } finally {
      setActionLoading(false);
    }
  };
  const getUserStats = () => {
    // Always use allUsers for stats calculation, not filtered users
    const stats = allUsers.reduce((acc, user) => {
      acc[user.role] = (acc[user.role] || 0) + 1;
      return acc;
    }, {});
    return {
      total: allUsers.length,
      admin: stats.admin || 0,
      superuser: stats.superuser || 0,
      user: stats.user || 0
    };
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="calculation-card">
          <div className="card-header">
            <h2><span className="loading-spinner"></span> Loading Users...</h2>
          </div>
        </div>
      </div>
    );
  }

  const stats = getUserStats();

  return (
    <div className="admin-container">
      <div className="calculation-card">
        <div className="admin-header">
          <h2 className='padding-10'>👥 Admin Dashboard</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onBackToDashboard} className="btn btn-secondary margin-10">
              ← Back to Dashboard
            </button>
          </div>
        </div>

        <div className="admin-stats padding-10">
          <div className="stat-card">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.admin}</div>
            <div className="stat-label">Admins</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.superuser}</div>
            <div className="stat-label">Superusers</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.user}</div>
            <div className="stat-label">Regular Users</div>
          </div>
        </div>

        {error && (
          <div className="message error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="search-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search users by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>

        {searchTerm && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <p style={{ margin: 0, color: '#64748b', textAlign: 'center' }}>
              {users.length > 0 
                ? `Found ${users.length} user${users.length !== 1 ? 's' : ''} matching "${searchTerm}"`
                : `No users found matching "${searchTerm}"`
              }
            </p>
          </div>
        )}

        {searchTerm && users.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <span className="user-id">#{user.id}</span>
                    </td>
                    <td>
                      <span className="user-email">{user.email}</span>
                    </td>
                    <td>
                      <span className={`role-badge ${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {currentUserRole === 'admin' && (
                          <button
                            onClick={() => handleRoleChange(user)}
                            className="btn-small btn-role"
                            disabled={actionLoading}
                          >
                            👤 Role
                          </button>
                        )}
                        <button
                          onClick={() => handlePasswordChange(user)}
                          className="btn-small btn-password"
                          disabled={actionLoading}
                        >
                          🔑 Password
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          className="btn-small btn-delete"
                          disabled={actionLoading}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showPasswordModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>🔑 Change Password for {selectedUser?.email}</h3>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="modal-buttons">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="btn btn-secondary h-3rem"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={submitPasswordChange}
                  className="btn btn-primary h-3rem"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRoleModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>👤 Change Role for {selectedUser?.email}</h3>
              <div className="form-group">
                <label className="form-label">Select New Role</label>
                <select
                  className="form-input"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <option value="user">User</option>
                  {currentUserRole === 'admin' && (
                    <>
                      <option value="superuser">Superuser</option>
                    </>
                  )}
                </select>
              </div>
              <div className="modal-buttons">
                <button
                  onClick={() => setShowRoleModal(false)}
                  className="btn btn-secondary h-3rem"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={submitRoleChange}
                  className="btn btn-primary h-3rem"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Updating...' : 'Update Role'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;